
import {
    FetchDocumentOptions,
    IDocument,
    IDocumentCommit,
    IDocumentStore,
    IDocumentTransaction,
    MemoRemoteInterface
} from '../IMemoRemote';
import {compareObjectsVerbose, splitItemRef} from '../util/CoreUtils';
import {arraySignal, CallableDisposableStack, observable, signal, SignalFunc,
    StackDisposable
} from '../lib/signal';
import {idbGet, idbPut, idbWrapRequest, idbSearch, idbUpdate} from '../util/IDBUtils';
import {slot} from "../lib/slot";
import {PropertyObjectWithId} from "../lib/memoTypes";

export function documentIdRange(docId: string): IDBKeyRange {
    return IDBKeyRange.bound([docId], [docId, []]);
}
export type DocumentInfo = { document: string, cursor?: number, t?: number, path?: string, title?: string };
@StackDisposable.Mixin
export class DocumentStore implements IDocumentStore, StackDisposable {
    declare [Symbol.dispose]: CallableDisposableStack;
    declare dtor: DisposableStack;

    @observable
    public accessor backingRemote: MemoRemoteInterface;

    public readonly errorOccured = signal<(errMes: string) => void>();

    ////////////////////    DOCUMENT  DB    ////////////////////////////////////////////////////////////

    public autoSyncLocal = true;

    private subscribedDocuments = new Set<string>();

    public readonly remoteItemsUpdated = signal<(items: PropertyObjectWithId[], documentId: string) => void>();
    public readonly collectOpenDocuments = arraySignal<() => string[]>();

    public constructor(public readonly indexedDb: IDBDatabase) {
    }

    @slot('backingRemote', $ => $.connectionEstablished)
    async onBackingRemoteConnected() {
        this.subscribedDocuments.clear();
        await this.fetchDocuments({
            local: this.autoSyncLocal,
            docs: this.collectOpenDocuments.flatCollect().filter(x => typeof x === 'string')
        });
    }

    async fetchUpdates(docVersions: [string, number][], subscribeToUpdates = true): Promise<void> {
        const updates = await this.backingRemote.getUpdates(docVersions, subscribeToUpdates);
        const failedDocs = new Set();
        for (const doc of updates.docs) {
            if ("server_error" in doc) {
                this.errorOccured.emit("" + doc.server_error);
                failedDocs.add(doc.document);
            } else {
                await this.updateReceived({}, doc.document, doc.items, doc.cursor);
            }
        }

        // Mark all requested documents, that didn't result in an error, as subscribed.
        for (const [docId, _] of docVersions) {
            if (!failedDocs.has(docId)) this.subscribedDocuments.add(docId);
        }
    }

    @slot('backingRemote', $ => $.event<[string, any[], number]>('pb', 'pb', 'doc_event'))
    async updateReceived(ctx: any, docId: string, items: any[], cursor: number) {
        console.log("updateReceived", ctx, docId, items, cursor) // XXX
        const changeset: PropertyObjectWithId[] = [];
        for (var k in items) {
            var recvItem = items[k];
            recvItem.document = docId;
            var localItem = await this.getItem(docId, recvItem.id);

            if (
                // item doesn't exist locally,
                !localItem

                // ...or item doesn't have local changes,
                // ...or item has local changes but local commit timestamp is older or equal then recv'd item's change server timestamp
                || Math.max(localItem.lmt || 0, localItem.mt || 0) <= recvItem.mt
            ) {

                if (
                    // item has local changes but local commit timestamp is older or equal then recv'd item's change server timestamp
                    localItem && localItem.lmt > localItem.mt
                ) {
                    // put up a warning but overwrite
                    const diff = compareObjectsVerbose(localItem, recvItem);
                    console.warn("Sync Conflict! Overriding local item with newer remote changes: ", diff);
                    idbPut(this.indexedDb, "syncConflicts", {
                        document: docId,
                        id: recvItem.id,
                        ts: Date.now(),
                        diff,
                        winner: "remote"
                    });
                    this.errorOccured.emit(`Sync Conflict in ${docId} (${recvItem.id})! Overriding local item with newer remote changes: ` + JSON.stringify(diff));
                }
                recvItem.st = +new Date();
                await this.storeItem(docId, recvItem);
                changeset.push(recvItem);

            } else if (
                // Server timestamp we have locally is equal to server timestamp recv'd, so nothing new from remote
                // but ....
                //    (local.mt == recv.mt && max(local.lmt, local.mt) > recv.mt) ==> (local.lmt > recv.mt && local.lmt > local.mt)
                //   ... we have local changes (local.lmt > local.mt), otherwise we had fallen into the branch above
                localItem.mt === recvItem.mt
            ) {
                console.warn(localItem.lmt || 0, localItem.mt || 0, recvItem.mt, `Local unsaved changes in ${docId} (${recvItem.id}): `, compareObjectsVerbose(recvItem, localItem));
                this.errorOccured.emit(`Unsaved local item in ${docId} (${recvItem.id})`);
            } else
                // we have local changes (local.lmt > local.mt)  and we have remote changes (remote.mt > local.mt)
                // but our local changes are newer (local.lmt > recv.mt), so we don't want to overwrite them
            {
                const diff = compareObjectsVerbose(localItem, recvItem);
                console.warn(localItem.lmt || 0, localItem.mt || 0, recvItem.mt, "Sync Conflict! Ignoring older remote changes: ", diff);
                this.errorOccured.emit(`Sync Conflict in ${docId} (${recvItem.id})! Ignoring older remote changes: ` + JSON.stringify(diff));
            }
        }
        this.remoteItemsUpdated.emit(changeset, docId);

        if (cursor) await idbUpdate<DocumentInfo>(
            this.indexedDb, "documents", docId,
            doc => Object.assign(doc || {document: docId}, {cursor})
        );
        console.log(`${docId}: ${items.length} items updated`);
    }

    /**
     * store a set of changes in a document into the local indexedDb and also directly send them onwards to the backingRemote
     *
     * the forward to backingRemote happens asynchronously (promise is not awaited)
     *
     * called by DbDocument, for changes which come, via DocumentTransaction, from the frontend
     */
    async performChanges(documentId: string, changes: PropertyObjectWithId[]) {
        for (const newdata of changes) {
            await this.storeItem(documentId, newdata);
        }
        console.log("uploading", changes.length);

        /* no-await */ this.uploadChanges(documentId, changes);
    }


    /**
     * sends a set of changes in a document to the backingRemote
     *
     * this probably belongs into a separate DocumentSyncManager or whatever?
     *
     * also, this should send all relevant changes in the document not only the most recent?
     *
     * TODO: new sync
     *
     * @param changes
     * @param remote_command
     * @private
     */
    private async uploadChanges(documentId: string, changes: PropertyObjectWithId[], remote_command?: string) {
        try {
            var response = await this.backingRemote.onSendObjects(documentId, changes, remote_command);
            console.log("upload successful", response, changes.length)
            if (!response.mt) throw new Error("invalid response to onSendObjects (missing mt)");

            const lmtUpdates: PropertyObjectWithId[] = [];
            for (const item of changes) {
                const dbItem = await this.updateItem(documentId, item.id, (dbItem) => {
                    dbItem.mt = response.mt;
                    if (dbItem.lmt === item.lmt) delete dbItem['lmt'];
                    else console.warn(`sendObject race ${dbItem.lmt} !== ${item.lmt} `);
                    return dbItem;
                });
                if (response.cursor) await idbUpdate<DocumentInfo>(
                    this.indexedDb, "documents", documentId,
                    doc => Object.assign(doc || {document: documentId}, {cursor: response.cursor})
                );
                lmtUpdates.push(dbItem);
            }
            this.remoteItemsUpdated.emit(lmtUpdates, documentId);
            return response;
        } catch (e) {
            console.warn("DocumentStore.uploadChanges",documentId,"Failed:",e)
            throw e;
        }
    }

    public readonly syncManager = {
        /**
         * legacy, but sadly still necessary :/
         * can be removed once the new sync is implemented that doesn't need manual save_document command
         */
        queryForUploadAll: async (cancel: AbortSignal): Promise<(PropertyObjectWithId & {document: string})[]> => {
            return await idbSearch(
                this.indexedDb,
                'items',
                cursor => (!!cursor.value.lmt) && (cursor.value.lmt > (+cursor.value.mt || 0)),
                IDBKeyRange.lowerBound(1), 0, 'lmt', 'next', cancel
            );
        },

        /**
         * this should be called with the full or filtered result of queryForUploadAll.
         * split in two functions so we can put a user interaction in between.
         */
        uploadAllChanges: async (changes: (PropertyObjectWithId & {document: string})[]) => {
            const docs = {};
            for(const change of changes) {
                docs[change.document!! as string] ??= [];
                docs[change.document!! as string].push(change);
            }
            for(const doc in docs) {
                await this.uploadChanges(doc, docs[doc], "save_document");
            }
        },

        removeFromSyncQueue: async (changes: (PropertyObjectWithId & {document: string})[]) => {
            for(const change of changes) {
                await this.updateItem(change.document, change.id, (dbItem) => {
                    delete dbItem['lmt'];
                    return dbItem;
                });
            }
        },
    };


    async storeItem(docId: string, item: any): Promise<IDBValidKey> {
        item = Object.assign({}, item, {document: docId});
        try {
            await this.updateDocumentInfoFromItem(docId, item);
            return await idbPut(this.indexedDb, "items", item);
        } catch (ex) {
            console.error("storeItem failed for ", item, ex);
            throw ex;
        }
    }

    async updateItem(docId: string, itemId: string, modifier: (item: any) => any): Promise<any> {
        const item = await idbUpdate(this.indexedDb, "items", [docId, itemId], (item: any): any => {
            item = modifier(item);
            return item;
        });
        await this.updateDocumentInfoFromItem(docId, item);
        return item;
    }

    private async updateDocumentInfoFromItem(docId: string, item: any) {
        await idbUpdate<DocumentInfo>(this.indexedDb, "documents", docId, doc => {
            doc = doc || {document: docId};

            // update last-change timestamp
            doc.t = Math.max(+(doc?.t as number) || 0, +item.t);

            // update from meta item
            if (item.id === 'meta') {
                doc.title = item.title;
                doc.path = item.path;
            }

            return doc;
        });
    }

    async getDocumentInfo(docId: string): Promise<DocumentInfo> {
        return idbGet<DocumentInfo>(this.indexedDb, "documents", docId);
    }

    getItem(docId: string, itemId?: string): Promise<any> {
        if (itemId === undefined) {
            [docId, itemId] = splitItemRef(docId);
        }
        return idbGet<any>(this.indexedDb, "items", [docId, itemId]);
    }

    getDocumentIds(prefix?: string): Promise<string[]> {
        return idbWrapRequest(this.indexedDb.transaction("documents").objectStore("documents").getAllKeys(
            prefix ? IDBKeyRange.bound(prefix, prefix + '\uffff') : undefined
        )) as Promise<string[]>;
    }

    async getDocuments(): Promise<{ [docId: string]: DocumentInfo }> {
        const docs = await idbWrapRequest<DocumentInfo[]>(this.indexedDb.transaction("documents").objectStore("documents").getAll());
        return Object.fromEntries(docs.map(doc => [doc.document, doc]));
    }

    async getItems(docId: string): Promise<{ items: any[], lmt: number, mt: number }> {
        console.time("getItems: " + docId)
        let lmt = 0;
        let mt = 0;
        let items = await this.searchItems((cursor) => {
            if (cursor.key[0] !== docId) {
                throw new Error("searchItems returned item outside range")
            }
            lmt = Math.max(lmt, cursor.value.lmt || 0);
            mt = Math.max(mt, cursor.value.mt || 0);
            return !cursor.value.deleted;
        }, documentIdRange(docId));
        console.timeEnd("getItems: " + docId)
        return {items, lmt, mt};
    }

    searchItems(filterFunction: (cursor: IDBCursorWithValue) => boolean | object, query?: IDBKeyRange, limit: number = 0, cancel?: AbortSignal): Promise<any[]> {
        return idbSearch(this.indexedDb, 'items', filterFunction, query, limit, query ? undefined : 't', query ? 'next' : 'prev', cancel);
    }

    async fetchDocuments(syncOptions: FetchDocumentOptions) {
        console.log("fetchDocuments: syncOptions = ", syncOptions);
        let localDocs;
        const fetchCursors: { [id: string]: number } = {};
        if (syncOptions.local) {
            localDocs = await this.getDocuments();
            for (const id in localDocs) if (!this.subscribedDocuments.has(id)) fetchCursors[id] = +localDocs[id]?.cursor || 0;
        }
        if (syncOptions.docs) {
            for (const id of syncOptions.docs) {
                if (!(id in fetchCursors) && !this.subscribedDocuments.has(id)) {
                    const cursor = localDocs?.[id] || (await this.getDocumentInfo(id))?.cursor;
                    fetchCursors[id] = +cursor || 0;
                }
            }
        }
        if (syncOptions.mounts) {
            if (!localDocs) localDocs = await this.getDocuments();
            for (const {user} of syncOptions.mounts) {
                const remoteIds: string[] = await this.backingRemote.listDir(user);
                for (const id of remoteIds) if (!this.subscribedDocuments.has(id)) fetchCursors[id] = +localDocs[id]?.cursor || 0;
            }
        }
        const fetchCursorsArray = Object.entries(fetchCursors);
        if (fetchCursorsArray.length) {
            console.log("fetchDocuments: requesting ", fetchCursors);
            await this.fetchUpdates(fetchCursorsArray, true);
            console.log("fetchDocuments: done ", fetchCursors);
        } else {
            console.log("fetchDocuments: nothing to do");
        }
    }

    async clearLocalData(): Promise<undefined> {
        await idbWrapRequest(this.indexedDb.transaction("items", "readwrite").objectStore("items").clear());
        await idbWrapRequest(this.indexedDb.transaction("documents", "readwrite").objectStore("documents").clear());
        await idbWrapRequest(this.indexedDb.transaction("syncConflicts", "readwrite").objectStore("syncConflicts").clear());
        this.subscribedDocuments.clear();
    }

}
